import { Helmet } from 'react-helmet-async';

const SITE_NAME = 'Affily';
const DEFAULT_DESCRIPTION =
  'The marketplace for creators and brands: partnership programs with clear terms, real audience stats, tracking and payouts in one place.';

/**
 * Client-side head management.
 *
 * The server already injected the same tags into the HTML before it reached the
 * browser (server/src/ssr/metaInject.js) — crawlers never depend on this
 * component. It keeps the head correct during client-side navigation, and it is
 * what the build-time prerender captures for static pages.
 */
export function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonical,
  type = 'website',
  noindex = false,
  jsonLd,
  /** The page's language, for a page that is not in English (a translated post). */
  lang = 'en',
  /** Every language this page is in, itself included: `[{ lang, path }]`, listed for search engines. */
  alternates = [],
}) {
  // Separator matches the server-side injector (server/src/ssr/metaInject.js),
  // so a title looks the same whether it was prerendered, injected, or set
  // client-side during navigation.
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} — Creators that sell. Brands that pay.`;
  // The build-time prerender has no window; it renders for the live site.
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://affily.me';
  const url =
    canonical ??
    (typeof window !== 'undefined' ? window.location.origin + window.location.pathname : undefined);

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {url && <link rel="canonical" href={url} />}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      {alternates.length > 1 &&
        alternates.map((a) => <link key={a.lang} rel="alternate" hrefLang={a.lang} href={origin + a.path} />)}

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      {url && <meta property="og:url" content={url} />}

      {/* The preview image is the same Affily card on every page; the server
          adds it (shareCardTags in server/src/ssr/metaInject.js). */}
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />

      {jsonLd && <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>}
    </Helmet>
  );
}
